import React from 'react';
import ReactDOM from 'react-dom/client';
import { Icon } from './components/ui.jsx';
import { api, fmtD, getToken, logout } from './api.js';
import { SL, RL } from './constants.js';
import { Dashboard } from './pages/dashboard.jsx';
import { ProjectsPage } from './pages/projects.jsx';
import { CalendarPage } from './pages/calendar.jsx';
import { TechsPage } from './pages/technicians.jsx';
import { ClientsPage } from './pages/clients.jsx';
import { MaterialsPage } from './pages/materials.jsx';
import { AvailPage } from './pages/availability.jsx';
import { AccountPage } from './pages/account.jsx';
import { ArchivePage } from './pages/archive.jsx';
import { WorkloadPage } from './pages/workload.jsx';
import { GraphPage } from './pages/graph.jsx';
import { AssistantPage } from './pages/assistant.jsx';
import { SettingsPage } from './pages/settings.jsx';
import { LoginPage } from './pages/login.jsx';
import { UsersPage } from './pages/users.jsx';
import { QuotesPage } from './pages/quotes.jsx';
import { AssetAvailPage } from './pages/asset-availability.jsx';

const NAV=[
  {section:'Produzione'},
  {id:'dashboard',label:'Dashboard',icon:'layout-dashboard'},
  {id:'projects',label:'Cantieri',icon:'clapperboard'},
  {id:'quotes',label:'Preventivi',icon:'file-text'},
  {id:'calendar',label:'Calendario',icon:'calendar'},
  {section:'Organizzazione'},
  {id:'techs',label:'Tecnici',icon:'users'},
  {id:'clients',label:'Clienti',icon:'building'},
  {id:'materials',label:'Attrezzature & Mezzi',icon:'package'},
  {id:'availability',label:'Disponibilità personale',icon:'calendar-check'},
  {id:'asset-availability',label:'Disponibilita mezzi',icon:'calendar-clock'},
  {section:'Analisi'},
  {id:'account',label:'Account Manager',icon:'trending-up'},
  {id:'workload',label:'Carichi lavoro',icon:'activity'},
  {id:'archive',label:'Archivio',icon:'archive'},
  {section:'Intelligence'},
  {id:'graph',label:'Grafo',icon:'git-branch'},
  {id:'assistant',label:'Assistente AI',icon:'message-circle'},
  {section:'Sistema'},
  {id:'users',label:'Utenti',icon:'shield'},{id:'settings',label:'Impostazioni',icon:'settings'},
];

// ── Global Search (Cmd+K) ─────────────────────────
const GlobalSearch = ({onClose, onNav}) => {
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState({projects:[], techs:[], clients:[]});
  const inputRef = React.useRef(null);

  React.useEffect(() => { inputRef.current?.focus(); }, []);

  React.useEffect(() => {
    if (q.length < 2) { setResults({projects:[],techs:[],clients:[]}); return; }
    const timer = setTimeout(async () => {
      try {
        const [projects, techs, clients] = await Promise.all([
          api('/projects'), api('/technicians'), api('/clients')
        ]);
        const lq = q.toLowerCase();
        setResults({
          projects: projects.filter(p =>
            (p.title||'').toLowerCase().includes(lq) ||
            (p.code||'').toLowerCase().includes(lq) ||
            (p.client_name||'').toLowerCase().includes(lq) ||
            (p.location||'').toLowerCase().includes(lq) ||
            (p.manager_name||'').toLowerCase().includes(lq)
          ).slice(0, 6),
          techs: techs.filter(t =>
            (t.name||'').toLowerCase().includes(lq) ||
            (t.email||'').toLowerCase().includes(lq) ||
            (t.phone||'').toLowerCase().includes(lq)
          ).slice(0, 4),
          clients: clients.filter(c =>
            (c.name||'').toLowerCase().includes(lq) ||
            (c.contact||'').toLowerCase().includes(lq)
          ).slice(0, 3),
        });
      } catch(e) {}
    }, 180);
    return () => clearTimeout(timer);
  }, [q]);

  const total = results.projects.length + results.techs.length + results.clients.length;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(14,16,40,.5)',
      backdropFilter:'blur(4px)',zIndex:400,display:'flex',
      alignItems:'flex-start',justifyContent:'center',paddingTop:'12vh'}}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{background:'var(--card)',borderRadius:14,width:540,maxWidth:'95vw',
        boxShadow:'0 20px 60px rgba(0,0,0,.25)',border:'1px solid var(--border)',overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border-soft)',
          display:'flex',alignItems:'center',gap:10}}>
          <Icon name="search" size={16} color="var(--n400)"/>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)}
            placeholder="Cerca cantieri, tecnici, clienti..."
            style={{flex:1,border:'none',outline:'none',fontSize:15,
              fontFamily:'var(--font)',background:'transparent',color:'var(--n800)'}}/>
          <kbd style={{background:'var(--n100)',padding:'2px 8px',borderRadius:4,
            fontSize:10,color:'var(--n400)',border:'1px solid var(--border)'}}>ESC</kbd>
        </div>
        {total > 0 && (
          <div style={{maxHeight:400,overflowY:'auto',padding:'8px 0'}}>
            {results.projects.length > 0 && <>
              <div style={{padding:'6px 18px',fontSize:10,color:'var(--n400)',fontWeight:700,
                textTransform:'uppercase',letterSpacing:'.06em'}}>Cantieri</div>
              {results.projects.map(p => (
                <div key={p.id} onClick={()=>{onNav('projects',p.id);onClose();}}
                  style={{padding:'8px 18px',cursor:'pointer',display:'flex',
                    alignItems:'center',gap:10}}
                  className="search-result">
                  <Icon name="clapperboard" size={14} color="var(--blue)"/>
                  <div style={{flex:1}}>
                    <span style={{fontWeight:600,fontSize:13}}>{p.title}</span>
                    <span style={{fontSize:11,color:'var(--n400)',marginLeft:8}}>{p.code}</span>
                  </div>
                  <span style={{fontSize:11,color:'var(--n400)'}}>{fmtD(p.date_start)}</span>
                </div>
              ))}
            </>}
            {results.techs.length > 0 && <>
              <div style={{padding:'6px 18px',fontSize:10,color:'var(--n400)',fontWeight:700,
                textTransform:'uppercase',letterSpacing:'.06em'}}>Tecnici</div>
              {results.techs.map(t => (
                <div key={t.id} onClick={()=>{onNav('techs');onClose();}}
                  style={{padding:'8px 18px',cursor:'pointer',display:'flex',
                    alignItems:'center',gap:10}}
                  className="search-result">
                  <Icon name="user" size={14} color="var(--n500)"/>
                  <span style={{fontWeight:600,fontSize:13}}>{t.name}</span>
                  <span style={{fontSize:11,color:'var(--n400)'}}>{t.email||''}</span>
                </div>
              ))}
            </>}
            {results.clients.length > 0 && <>
              <div style={{padding:'6px 18px',fontSize:10,color:'var(--n400)',fontWeight:700,
                textTransform:'uppercase',letterSpacing:'.06em'}}>Clienti</div>
              {results.clients.map(c => (
                <div key={c.id} onClick={()=>{onNav('clients');onClose();}}
                  style={{padding:'8px 18px',cursor:'pointer',display:'flex',
                    alignItems:'center',gap:10}}
                  className="search-result">
                  <Icon name="building" size={14} color="var(--n500)"/>
                  <span style={{fontWeight:600,fontSize:13}}>{c.name}</span>
                </div>
              ))}
            </>}
          </div>
        )}
        {q.length >= 2 && total === 0 && (
          <div style={{padding:'24px 18px',textAlign:'center',color:'var(--n400)',fontSize:13}}>
            Nessun risultato per "<strong>{q}</strong>"
          </div>
        )}
      </div>
    </div>
  );
};

// ── App Shell ────────────────────────────────────
const App = () => {
  const [page, setPage] = React.useState('dashboard');
  const [openId, setOpenId] = React.useState(null);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [auth, setAuth] = React.useState({checking: true, authenticated: false, passwordSet: false});
  const [notifs, setNotifs] = React.useState({pending:0, messages:0});
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [notifFeed, setNotifFeed] = React.useState(null);
  const notifRef = React.useRef(null);
  
  // Chiudi dropdown al click fuori
  React.useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  
  const openNotifs = async () => {
    if (notifOpen) { setNotifOpen(false); return; }
    setNotifOpen(true);
    try {
      const d = await api('/notifications/feed');
      setNotifFeed(d);
    } catch(e) { setNotifFeed({pending:[],messages:[],responses:[]}); }
  };
  const nav = (p, id=null) => { setPage(p); setOpenId(id); };

  // Auth check on mount
  React.useEffect(() => {
    const token = getToken();
    fetch('/api/auth/check', {headers: token ? {'Authorization': 'Bearer ' + token} : {}})
      .then(r => r.json())
      .then(d => setAuth({checking: false, authenticated: d.authenticated, passwordSet: d.password_set}))
      .catch(() => setAuth({checking: false, authenticated: false, passwordSet: false}));
    // Listen for auth expiry
    const onExpired = () => setAuth(a => ({...a, authenticated: false}));
    window.addEventListener('ejatv-auth-expired', onExpired);
    return () => window.removeEventListener('ejatv-auth-expired', onExpired);
  }, []);

  // Poll notifications (update solo se cambiano)
  React.useEffect(() => {
    if (!auth.authenticated) return;
    const poll = () => {
      fetch('/api/notifications/count', {headers:{'Authorization':'Bearer '+(getToken()||'')}})
        .then(r=>r.json())
        .then(d => setNotifs(prev => {
          if (prev.pending === d.pending && prev.messages === d.messages) return prev;
          return d;
        }))
        .catch(()=>{});
    };
    poll();
    const iv = setInterval(poll, 300000);
    return () => clearInterval(iv);
  }, [auth.authenticated]);

  React.useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(s => !s); }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Show login if password is set and not authenticated
  if (auth.checking) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{color:'#94a3b8'}}>Caricamento...</div></div>;
  if (auth.passwordSet && !auth.authenticated) return <LoginPage onLogin={() => setAuth({checking:false,authenticated:true,passwordSet:true})}/>;

  const P = () => {
    switch(page) {
      case 'dashboard':    return <Dashboard onNav={nav}/>;
      case 'projects':     return <ProjectsPage openId={openId} initModal={openId==='new'?'new':null}/>;
      case 'quotes':       return <QuotesPage onNav={nav}/>;
      case 'asset-availability': return <AssetAvailPage/>;
      case 'calendar':     return <CalendarPage onNav={nav}/>;
      case 'techs':        return <TechsPage/>;
      case 'clients':      return <ClientsPage/>;
      case 'materials':    return <MaterialsPage/>;
      case 'availability': return <AvailPage/>;
      case 'workload':     return <WorkloadPage onNav={nav}/>;
      case 'account':      return <AccountPage onNav={nav}/>;
      case 'graph':        return <GraphPage onNav={nav}/>;
      case 'archive':      return <ArchivePage onNav={nav}/>;
      case 'assistant':    return <AssistantPage/>;
      case 'users':        return <UsersPage/>;
      case 'settings':     return <SettingsPage/>;
      default:             return <Dashboard onNav={nav}/>;
    }
  };

  return(
    <>
      <div className="sidebar">
        <div className="sb-brand">
          <img src="/logo-tdp.svg" alt="Terra de Punt" className="sb-logo-img"/>
          <div className="sb-logo-sub">Broadcast Manager</div>
        </div>
        <div className="sb-nav" style={{flex:1,overflowY:'auto',paddingBottom:16,scrollbarWidth:'none',msOverflowStyle:'none'}}>
          {NAV.map((n,i) => n.section ? (
            <div key={i} className="sidebar-section">{n.section}</div>
          ) : (
            <div key={n.id} className={'nav-item'+(page===n.id?' active':'')}
              onClick={()=>nav(n.id)}>
              <Icon name={n.icon} size={16}
                color={page===n.id?'#fff':'rgba(255,255,255,0.45)'}/>
              <span>{n.label}</span>
            </div>
          ))}
        </div>
        <div onClick={()=>setSearchOpen(true)}
          style={{padding:'10px 18px',borderTop:'1px solid var(--sb-border)',
          fontSize:11,color:'var(--sb-text)',display:'flex',alignItems:'center',gap:8,
          cursor:'pointer',transition:'background .1s'}}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.04)'}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <Icon name="search" size={13} color="rgba(255,255,255,.3)"/>
          <span style={{flex:1}}>Cerca ovunque</span>
          <kbd style={{background:'rgba(255,255,255,.08)',padding:'2px 6px',
            borderRadius:4,fontSize:9,border:'1px solid rgba(255,255,255,.12)',
            color:'rgba(255,255,255,.35)'}}>⌘K</kbd>
        </div>
        {auth.authenticated && (
          <div onClick={logout}
            style={{padding:'8px 18px',borderTop:'1px solid var(--sb-border)',
            fontSize:11,color:'rgba(255,255,255,.3)',display:'flex',alignItems:'center',gap:8,
            cursor:'pointer',transition:'all .1s'}}
            onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,.04)';e.currentTarget.style.color='rgba(255,255,255,.6)';}}
            onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,.3)';}}>
            <Icon name="log-out" size={13}/>
            <span>Esci</span>
          </div>
        )}
      </div>
      <div className="main">
        {/* Topbar — SCAN360 pattern */}
        <div className="topbar">
          <div className="topbar-breadcrumb">
            <span>EjaTV</span>
            <span className="topbar-breadcrumb-sep">{'›'}</span>
            <span className="topbar-breadcrumb-current">{NAV.find(n=>n.id===page)?.label||'Dashboard'}</span>
          </div>
          <div className="topbar-title"/>
          <div className="topbar-date">
            {new Date().toLocaleDateString('it-IT',{day:'numeric',month:'long',year:'numeric'})}
          </div>
          <div className="topbar-sep"/>
          <div ref={notifRef} style={{position:'relative'}}>
            <button className="topbar-icon-btn" onClick={openNotifs} title="Notifiche">
              <Icon name="bell" size={18}/>
              {(notifs.pending+notifs.messages)>0&&<span className="topbar-badge">{notifs.pending+notifs.messages}</span>}
            </button>
            {notifOpen&&(
              <div style={{position:'absolute',top:'calc(100% + 8px)',right:0,width:380,
                background:'var(--card)',border:'1px solid var(--border)',borderRadius:12,
                boxShadow:'0 8px 32px rgba(0,0,0,.15)',zIndex:999,maxHeight:480,overflowY:'auto'}}>
                <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',
                  fontWeight:700,fontSize:14,color:'var(--n800)',
                  display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>Notifiche</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setNotifOpen(false)}
                    style={{padding:'4px'}}><Icon name="x" size={14} color="var(--n400)"/></button>
                </div>
                {!notifFeed&&<div style={{padding:20,textAlign:'center',color:'var(--n400)',fontSize:13}}>Caricamento...</div>}
                {notifFeed&&(
                  <div>
                    {/* Conferme in attesa */}
                    {notifFeed.pending.length>0&&(
                      <div>
                        <div style={{padding:'8px 16px',fontSize:10,fontWeight:700,color:'var(--n400)',
                          textTransform:'uppercase',letterSpacing:'.06em',background:'var(--n50)'}}>
                          Conferme in attesa ({notifFeed.pending.length})
                        </div>
                        {notifFeed.pending.map(n=>(
                          <div key={n.crew_id} style={{padding:'10px 16px',borderBottom:'1px solid var(--n100)',
                            cursor:'pointer',display:'flex',alignItems:'center',gap:10}}
                            onClick={()=>{nav('projects',n.project_id);setNotifOpen(false);}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:'#F59E0B',flexShrink:0}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:'var(--n800)',
                                overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                {n.technician_name}</div>
                              <div style={{fontSize:11,color:'var(--n400)'}}>
                                {n.project_code} — {n.project_title}
                                {n.date_start&&' · '+n.date_start.split('T')[0].split('-').reverse().join('/')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Risposte tecnici */}
                    {notifFeed.responses.length>0&&(
                      <div>
                        <div style={{padding:'8px 16px',fontSize:10,fontWeight:700,color:'var(--n400)',
                          textTransform:'uppercase',letterSpacing:'.06em',background:'var(--n50)'}}>
                          Risposte recenti ({notifFeed.responses.length})
                        </div>
                        {notifFeed.responses.map((n,i)=>(
                          <div key={i} style={{padding:'10px 16px',borderBottom:'1px solid var(--n100)',
                            cursor:'pointer',display:'flex',alignItems:'center',gap:10}}
                            onClick={()=>{nav('projects',n.project_id);setNotifOpen(false);}}>
                            <span style={{width:8,height:8,borderRadius:'50%',
                              background:n.letter_status==='accepted'?'#10B981':'#EF4444',flexShrink:0}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:'var(--n800)'}}>
                                {n.technician_name} — {n.letter_status==='accepted'?'confermato':'declinato'}</div>
                              <div style={{fontSize:11,color:'var(--n400)'}}>
                                {n.project_code} — {n.project_title}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Messaggi recenti */}
                    {notifFeed.messages.length>0&&(
                      <div>
                        <div style={{padding:'8px 16px',fontSize:10,fontWeight:700,color:'var(--n400)',
                          textTransform:'uppercase',letterSpacing:'.06em',background:'var(--n50)'}}>
                          Messaggi recenti ({notifFeed.messages.length})
                        </div>
                        {notifFeed.messages.map(m=>(
                          <div key={m.id} style={{padding:'10px 16px',borderBottom:'1px solid var(--n100)',
                            cursor:'pointer',display:'flex',alignItems:'center',gap:10}}
                            onClick={()=>{nav('projects',m.project_id);setNotifOpen(false);}}>
                            <Icon name="message-square" size={14} color="var(--blue)"/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:600,color:'var(--n800)',
                                overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                {m.subject||'Comunicazione'}</div>
                              <div style={{fontSize:11,color:'var(--n400)'}}>
                                {m.project_code} · {m.created_at?.split('T')[0].split('-').reverse().join('/')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Vuoto */}
                    {!notifFeed.pending.length&&!notifFeed.messages.length&&!notifFeed.responses.length&&(
                      <div style={{padding:30,textAlign:'center',color:'var(--n400)',fontSize:13}}>
                        <Icon name="check-circle" size={24} color="var(--n300)" style={{marginBottom:8}}/>
                        <div>Nessuna notifica</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="topbar-sep"/>
          <button className="topbar-profile-btn" onClick={()=>nav('settings')} title="Profilo">
            <Icon name="settings" size={16}/>
            <span>Admin</span>
          </button>
        </div>
        {/* Insight Strip */}
        <div className="insight-strip">
          <div className="insight-strip__item" onClick={()=>nav('projects')}>
            <span className={'insight-strip__dot insight-strip__dot--'+(notifs.pending>0?'warning':'success')}/>
            <span className="insight-strip__text"><strong>{notifs.pending||0}</strong> conferme in attesa</span>
            {notifs.pending>0&&<span className="insight-strip__cta">Vedi →</span>}
          </div>
          <div className="insight-strip__item" onClick={()=>nav('workload')}>
            <span className="insight-strip__dot insight-strip__dot--blue"/>
            <span className="insight-strip__text">Carichi lavoro aggiornati</span>
            <span className="insight-strip__cta">Apri →</span>
          </div>
          <div className="insight-strip__item" onClick={()=>nav('archive')}>
            <span className="insight-strip__dot insight-strip__dot--success"/>
            <span className="insight-strip__text">Archivio cantieri</span>
            <span className="insight-strip__cta">Apri →</span>
          </div>
        </div>
        <div className="main-content">
          {searchOpen && <GlobalSearch onClose={()=>setSearchOpen(false)} onNav={nav}/>}
          {P()}
        </div>
      </div>
    </>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);
